import { Divider, Select } from "antd";
import { CreditCard } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { updateField } from "../../../redux/slices/employee-slice";
import { useState, useEffect, useCallback } from "react";
import ModalWrapper from "../../../Modal";
import PaySchedule from "../../../modals/pay-schedule";
import Contribution from "../../../modals/contribution";
import AddExtra from "../../../modals/addExtra";
import { getRequest } from "../../../service/index";
import {countries} from '../../../constant/countery'
import { getEmployeeData } from  "../../../utils/localStorage"

const Salary = () => {
  const dispatch = useDispatch();
  const form = useSelector((state) => state.employeeForm);
  const [addPayPeriod, setPayPeriod] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [contributionOpen, setContributionOpen] = useState(false);
  const [selectedAdditions, setSelectedAdditions] = useState([]);
  const [salaryAdditional, setSalaryAdditional] = useState([]);
  const [workingHours, setWorkingHours] = useState(9); // Add working hours state with default 9
  const data = getEmployeeData();
  const tableHeadings = ["Name", "Percentage", "Amount", "Type"];
  const currency = data.currency;
  const country = countries.find(c => c.currency === currency);
  const currencySign = country ? country.currencySymbol : currency;
  
  // Fetch salary additional data from API
  useEffect(() => {
    fetchSalaryAdditional();
  }, []);

  const fetchSalaryAdditional = async () => {
    try {
      const response = await getRequest('/api/taxandbonus');
      if (response && response.entries) {
        const formattedData = response.entries.map(item => ({
          value: item._id,
          label: item.title,
          type: item.type === 'deductions' ? 'Deductions': item.type === 'contributions' ? 'Contributions' : 'Allowances',
          amount: item.amount,
          amountType: item.amountType,
          period: "monthly",
          calculateType: "not_fixed",
        }));
        setSalaryAdditional(formattedData);
      }
    } catch (error) {
      console.error('Error fetching salary additional data:', error);
    }
  };
  
  // Calculate working days in current month excluding weekends
  const getWorkingDays = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();

    let workingDays = 0;
    for (let day = 1; day <= lastDay; day++) {
      const date = new Date(year, month, day);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  };

  // Calculate per hour salary
  const calculatePerHourSalary = useCallback((monthlySalary, days) => {
    const totalWorkingHours = days * workingHours; // Use workingHours state instead of hardcoded 9
    return (monthlySalary / totalWorkingHours).toFixed(2);
  }, [workingHours]);

  const getTotalDays = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  };

  useEffect(() => {
    if (form.monthly_salary && form.salary_type === 'monthly') {
      const monthlyAmount = parseFloat(form.monthly_salary);
      if (!isNaN(monthlyAmount)) {
        let days;
        if (form.day_calculation === 'fixed') {
          days = parseInt(form.fixed_Days) || 0;
        } else {
          days = form.day_calculation === 'total' ? getTotalDays() : getWorkingDays();
        }
        if (days > 0) {
          const dailyRate = (monthlyAmount / days).toFixed(2);
          const hourlyRate = calculatePerHourSalary(monthlyAmount, days);
          dispatch(updateField({ name: "perday_salary", value: dailyRate }));
          dispatch(updateField({ name: "perhour_salary", value: hourlyRate }));
        }
      }
    }
  }, [form.monthly_salary, form.day_calculation, form.fixed_Days, form.salary_type, calculatePerHourSalary, dispatch]);

  // Initialize salary type in form state if not present
  useEffect(() => {
    if (!form.salary_type) {
      dispatch(updateField({ name: "salary_type", value: "monthly" }));
    }
  }, [form.salary_type, dispatch]);

  // Update selectedAdditions when form.salary_additional changes
  useEffect(() => {
    if (form.salary_additional) {
      const selected = salaryAdditional.filter(item => 
        form.salary_additional.includes(item.value)
      );
      setSelectedAdditions(selected);
    }
  }, [form.salary_additional, salaryAdditional]);

  const handleChange = (value, name) => {
    dispatch(updateField({ name, value }));
  };

  const salaryTypeOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'one_time', label: 'One Time' },
    { value: 'hourly', label: 'Hourly' },
  ];

  const daysTypeOptions = [
    { value: 'total', label: 'Total Days in Month' },
    { value: 'working', label: 'Working Days Only' },
    { value: 'fixed', label: 'Fixed Days' }
  ];

  const paidTypeOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'on_finish', label: 'On Finish' }
  ];

  const renderSalaryFields = () => {
    const currentSalaryType = form.salary_type || "";

    switch (currentSalaryType) {
      case 'hourly':
        return (
          <>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="hourlyRate" className="text-md">
                Hourly Rate
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "hourly_rate")}
                  value={form.hourly_rate || ""}
                />
              </div>
            </div>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="paidType" className="text-md">
                Paid Type
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                className="w-full"
                options={paidTypeOptions}
                value={form.paid_type || ""}
                onChange={(value) => handleChange(value, "paid_type")}
              />
            </div>
          </>
        );

      case 'monthly':
        return (
          <>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="daysType" className="text-md">
                Days Calculation
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                className="w-full"
                options={daysTypeOptions}
                value={form.day_calculation || ""}
                onChange={(value) => handleChange(value, "day_calculation")}
              />
            </div>

            {form.day_calculation === 'fixed' && (
              <div className="col-span-6 flex flex-col gap-1">
                <label htmlFor="fixedDays" className="text-md">
                  Number of Working Days
                  <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <input
                  type="number"
                  className="border px-2 py-1 rounded-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "fixed_Days")}
                  value={form.fixed_Days || ""}
                  placeholder="i.e 22"
                  min="1"
                  max="31"
                />
              </div>
            )}

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="workingHours" className="text-md">
                Working Hours per Day
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <input
                type="number"
                className="border px-2 py-1 rounded-md border-secondary text-md outline-none w-full bg-white"
                onChange={(e) => setWorkingHours(parseFloat(e.target.value) || 9)}
                value={workingHours}
                placeholder="i.e 9"
                min="1"
                max="24"
              />
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="weekdaySalary" className="text-md">
                Monthly Salary
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "monthly_salary")}
                  value={form.monthly_salary || ""}
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="perDaySalary" className="text-md">
                Per Day Salary
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  value={form.perday_salary || ""}
                  disabled
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="perhour_salary" className="text-md">
                Per Hour Salary
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  value={form.perhour_salary || ""}
                  disabled
                />
              </div>
            </div>
          </>
        );

      case 'one_time':
        return (
          <>
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="totalAmount" className="text-md">
                Total Amount
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "total_ammount")}
                  value={form.total_ammount || ""}
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="paidType" className="text-md">
                Paid Type
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                className="w-full"
                options={paidTypeOptions}
                value={form.paid_type || ""}
                onChange={(value) => handleChange(value, "paid_type")}
              />
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="paidAmount" className="text-md">
                Paid Amount
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "paid_ammount")}
                  value={form.paid_ammount || ""}
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="advancePayment" className="text-md">
                Advance Payment
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <div className="flex items-center">
                <span className="border px-2 py-1 rounded-l-md border-secondary text-md bg-primary text-white">
                  {currencySign}
                </span>
                <input
                  type="text"
                  className="border px-2 py-1 rounded-r-md border-secondary text-md outline-none w-full bg-white"
                  onChange={(e) => handleChange(e.target.value, "advance_payment")}
                  value={form.advance_payment || ""}
                />
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Calculate net salary based on additions and deductions
  const calculateNetSalary = () => {
    let grossSalary = parseFloat(form.monthly_salary) || 0;
    let totalAllowances = 0;
    let totalContributions = 0;
    let totalDeductions = 0;
    let taxableSalary = grossSalary;

    selectedAdditions.forEach(item => {
      const amount = parseFloat(item.amount);
      const calculatedAmount = item.amountType === 'percentage'
        ? (grossSalary * amount) / 100
        : amount;

      switch (item.type) {
        case 'Allowances':
          totalAllowances += calculatedAmount;
          taxableSalary += calculatedAmount;
          break;
        case 'Contributions':
          totalContributions += calculatedAmount;
          break;
        case 'Deductions':
          totalDeductions += calculatedAmount;
          taxableSalary -= calculatedAmount;
          break;
      }
    });

    // Update form state with calculated values
    dispatch(updateField({ name: "allowance", value: totalAllowances.toFixed(2) }));
    dispatch(updateField({ name: "deduction", value: totalDeductions.toFixed(2) }));
    dispatch(updateField({ name: "contribution", value: totalContributions.toFixed(2) }));
    dispatch(updateField({ name: "overAll_salary", value: (grossSalary + totalAllowances - totalContributions - totalDeductions).toFixed(2) }));

    return {
      grossSalary,
      totalAllowances,
      totalContributions,
      totalDeductions,
      taxableSalary,
      netSalary: grossSalary + totalAllowances - totalContributions - totalDeductions
    };
  };

  // Update salaryDetails with calculated values
  const updatedSalaryDetails = () => {
    const calculations = calculateNetSalary();
    const details = [
      {
        label: "Gross Salary",
        value: calculations.grossSalary,
      }
    ];

    // Add selected items to the details
    selectedAdditions.forEach(item => {
      const amount = parseFloat(item.amount);
      const calculatedAmount = item.amountType === 'percentage'
        ? (calculations.grossSalary * amount) / 100
        : amount;

      details.push({
        label: item.label,
        value: calculatedAmount,
        type: item.type
      });
    });

    // Add final calculations
    details.push(
      {
        label: "Total Allowances",
        value: calculations.totalAllowances,
        type: "summary"
      },
      {
        label: "Total Contributions",
        value: calculations.totalContributions,
        type: "summary"
      },
      {
        label: "Total Deductions",
        value: calculations.totalDeductions,
        type: "summary"
      },
      {
        label: "Net Salary",
        value: calculations.netSalary,
        type: "final"
      }
    );

    return details;
  };
  const onExtraClolse = () => {
    setAddNew(false);
    fetchSalaryAdditional();
  }
  return (
    <>
      <ModalWrapper
        onRequestClose={() => setPayPeriod(false)}
        open={addPayPeriod}
        title="Add Schedule"
      >
        <PaySchedule onClose={() => setPayPeriod(false)} />
      </ModalWrapper>

      <ModalWrapper
        open={contributionOpen}
        title="Contribution"
        onRequestClose={() => setContributionOpen(false)}
      >
        <Contribution onClose={() => setContributionOpen(false)} />
      </ModalWrapper>

      <ModalWrapper
        open={addNew}
        title="Add New Extra"
        onRequestClose={onExtraClolse}
      >
        <AddExtra onClose={onExtraClolse} />
      </ModalWrapper>

      <div className="flex items-start justify-between bg-white px-4">
        <div className="flex flex-col gap-3 mt-1">
          <CreditCard className="h-7 w-7 text-active-color stroke-1" />
          <h4 className="font-bold text-md">Salary Details</h4>
          <p className="text-gray-400 text-md -mt-2 max-w-48  line-clamp-2">
            Fill in the employee&apos;s salary details and pay schedule.
          </p>
        </div>
        <div className="bg-form-bg w-lg rounded-md px-4 flex flex-col gap-6 py-8 mt-5">
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-6 flex flex-col gap-1">
              <label htmlFor="salaryType" className="text-md">
                Salary Type
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                className="w-full"
                options={salaryTypeOptions}
                value={form.salary_type || "monthly"}
                onChange={(value) => handleChange(value, "salary_type")}
              />
            </div>

            {renderSalaryFields()}
          </div>
        </div>
      </div>

      {form.salary_type === 'monthly' && (
        <>
          <Divider />
          <div className="flex flex-col gap-3 ">
            <div className="flex items-center gap-3 w-full">
              <Select
                mode="multiple"
                showSearch
                className="w-full"
                placeholder="Search to Select"
                optionFilterProp="label"
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                options={salaryAdditional}
                value={form.salary_additional || []}
                onChange={(values) => {
                  handleChange(values, "salary_additional");
                }}
              />
              <button
                className="text-white bg-active-color px-4 py-1.5 cursor-pointer rounded-md text-md hover:bg-active-hover transition-colors duration-150 ease-linear whitespace-nowrap"
                onClick={() => setAddNew(true)}
              >
                Add New Extra
              </button>
            </div>

            <table>
              <thead>
                <tr className="rounded-md bg-tablehead">
                  {tableHeadings.map((heading) => (
                    <th
                      key={heading}
                      className={`${heading === "Add"
                        ? "px-4 py-2.5 text-md text-black"
                        : "px-4 py-2.5 text-md text-black"
                        }`}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedAdditions.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-4 py-2.5 text-md text-center">{item.label}</td>
                    <td className="px-4 py-2.5 text-md text-center">{item.amount}{item.amountType === 'percentage' ? '%' : currencySign}</td>
                    <td className="px-4 py-2.5 text-md text-center">
                      {item.amountType === 'percentage'
                        ? Math.round((form.monthly_salary * item.amount) / 100)
                        : item.amount} {currencySign}
                    </td>
                    <td className="px-4 py-2.5 text-md text-center">{item.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-start justify-between bg-white px-4   mt-32">
              <div className="flex flex-col gap-3 mt-1">
                <CreditCard className="h-7 w-7 text-active-color stroke-1" />
                <h4 className="font-semibold text-md">Final Salary Details</h4>
                <p className="text-gray-400 text-md -mt-2 max-w-48  line-clamp-2">
                  Payable by company to employee
                </p>
              </div>

              <div className="bg-form-bg w-xl rounded-md px-4 flex flex-col gap-6 py-8 mr-8 mt-5 relative">
                <div className="flex flex-col gap-1">
                  {updatedSalaryDetails().map((salary, index) => (
                    <div
                      className={`w-full flex justify-between items-center ${salary.type === 'summary' ? 'text-gray-600' :
                        salary.type === 'final' ? 'text-green-600 font-bold' : ''
                        }`}
                      key={index}
                    >
                      <h4 className="text-md">{salary.label}</h4>
                      <span className={`text-md font-semibold ${salary.type === 'summary' ? 'text-gray-600' :
                        salary.type === 'final' ? 'text-green-600' : 'text-gray-400'
                        }`}>
                        {salary.value.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </>
      )}
    </>
  );
};

export default Salary;
